/**
 * Microphone capture with simple energy-based voice activity detection.
 *
 * Frames come from an AudioWorklet. A frame counts as speech when it is clearly louder than the
 * (slowly tracked) noise floor. An utterance starts after a few speech frames, ends after a pause,
 * and is delivered as a 16 kHz 16-bit mono WAV. The server double-checks clips with Silero VAD.
 */

const WORKLET = `
class Tap extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) this.port.postMessage(ch.slice(0));
    return true;
  }
}
registerProcessor("webthing-tap", Tap);
`;

export interface CaptureOptions {
  onSpeechStart?: () => void;
  onUtterance: (wav: Blob, seconds: number) => void;
  onLevel?: (db: number, speaking: boolean) => void;
  /** Extra dB above the noise floor needed to count as speech (raised while the assistant talks). */
  extraThreshold?: () => number;
}

const START_FRAMES_MS = 120;
const END_SILENCE_MS = 900;
const PREROLL_MS = 300;
const MAX_SECONDS = 30;
const MIN_SECONDS = 0.35;

export class MicCapture {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private node: AudioWorkletNode | null = null;
  private floor = -60;
  private chunks: Float32Array[] = [];
  private preroll: Float32Array[] = [];
  private speaking = false;
  private loudMs = 0;
  private quietMs = 0;
  private paused = false;

  constructor(private opts: CaptureOptions) {}

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } });
    this.ctx = new AudioContext();
    const url = URL.createObjectURL(new Blob([WORKLET], { type: "application/javascript" }));
    await this.ctx.audioWorklet.addModule(url);
    URL.revokeObjectURL(url);
    const src = this.ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(this.ctx, "webthing-tap");
    this.node.port.onmessage = (e) => this.frame(e.data as Float32Array);
    src.connect(this.node);
  }

  stop(): void {
    this.node?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.ctx?.close().catch(() => undefined);
    this.node = null;
    this.stream = null;
    this.ctx = null;
    this.reset();
  }

  /** Ignore the microphone (e.g. while a clip is being transcribed in push-to-talk mode). */
  setPaused(p: boolean) {
    this.paused = p;
    if (p) this.reset();
  }

  private reset() {
    this.chunks = [];
    this.speaking = false;
    this.loudMs = 0;
    this.quietMs = 0;
  }

  private frame(data: Float32Array) {
    if (!this.ctx || this.paused) return;
    const rate = this.ctx.sampleRate;
    const ms = (data.length / rate) * 1000;
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
    const db = 10 * Math.log10(sum / data.length + 1e-12);
    // The floor follows quiet quickly and loud slowly.
    this.floor = db < this.floor ? this.floor * 0.9 + db * 0.1 : this.floor * 0.999 + db * 0.001;
    const threshold = Math.max(this.floor + 12 + (this.opts.extraThreshold?.() ?? 0), -50);
    const loud = db > threshold;
    this.opts.onLevel?.(db, this.speaking);

    if (!this.speaking) {
      this.preroll.push(data);
      let total = 0;
      for (let i = this.preroll.length - 1; i >= 0; i--) {
        total += (this.preroll[i].length / rate) * 1000;
        if (total > PREROLL_MS) {
          this.preroll = this.preroll.slice(i);
          break;
        }
      }
      this.loudMs = loud ? this.loudMs + ms : 0;
      if (this.loudMs >= START_FRAMES_MS) {
        this.speaking = true;
        this.quietMs = 0;
        this.chunks = [...this.preroll];
        this.preroll = [];
        this.opts.onSpeechStart?.();
      }
      return;
    }
    this.chunks.push(data);
    this.quietMs = loud ? 0 : this.quietMs + ms;
    const seconds = this.chunks.reduce((n, c) => n + c.length, 0) / rate;
    if (this.quietMs >= END_SILENCE_MS || seconds >= MAX_SECONDS) {
      const chunks = this.chunks;
      this.reset();
      if (seconds >= MIN_SECONDS) this.opts.onUtterance(encodeWav(chunks, rate, 16000), seconds);
    }
  }
}

export function encodeWav(chunks: Float32Array[], fromRate: number, toRate: number): Blob {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const merged = new Float32Array(total);
  let o = 0;
  for (const c of chunks) {
    merged.set(c, o);
    o += c.length;
  }
  const ratio = fromRate / toRate;
  const n = Math.floor(total / ratio);
  const pcm = new Int16Array(n);
  for (let i = 0; i < n; i++) {
    // Average the source samples that fall into this output sample (a crude low-pass).
    const a = Math.floor(i * ratio);
    const b = Math.min(total, Math.floor((i + 1) * ratio));
    let s = 0;
    for (let j = a; j < b; j++) s += merged[j];
    const v = Math.max(-1, Math.min(1, s / Math.max(1, b - a)));
    pcm[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  const header = new ArrayBuffer(44);
  const v = new DataView(header);
  const str = (off: number, s: string) => [...s].forEach((ch, i) => v.setUint8(off + i, ch.charCodeAt(0)));
  str(0, "RIFF");
  v.setUint32(4, 36 + pcm.byteLength, true);
  str(8, "WAVE");
  str(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, toRate, true);
  v.setUint32(28, toRate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  str(36, "data");
  v.setUint32(40, pcm.byteLength, true);
  return new Blob([header, pcm.buffer], { type: "audio/wav" });
}
